// @flow

import * as React from 'react';
import { bind } from '@ta/common.api';
import { ClickTracker } from '@ta/common.tracking';
import { featureIsEnabled } from "@ta/common.features";
import classNames from '@ta/common.classnames';
import { localize } from '@ta/common.i18n';
import { Link } from '@ta/common.routing';
import DataCacher from '@ta/common.data-cacher';
import { Environment } from '@ta/lithium.context';
import { t4bNavPages } from './T4BNav';
import styles from './Logo.less';

type Props = {|
  /** URL for the logo link. Defaults to "/" */
  logoLink?: string,

  /** Boolean indicating whether or not the data is still loading from the logo api call */
  loaded: boolean,

  /** Boolean indicating the data is loading */
  loading: boolean,

  /** Whether or not the device hit a mobile breakpoint */
  isMobileBreakpoint: boolean,

  /** Whether or not the user is using an obsolete browser (I.E. 10.0 or lower) */
  isObsoleteBrowser: boolean,

  /** Whether we're in the management center and should link to /Owners instead of / */
  isT4BNavPage: boolean,

  /** The url we are currently in which will be used for tracking */
  trackingUrl: string,
|};

/** Takes in the props and returns either a loading spinner if the data isn't ready, or the logo */
const Logo = (props: Props) => {
  const { cdnPrefix } = React.useContext(Environment);

  if (!props.loaded) {
    return null;
  }
  const imageClass =  featureIsEnabled('dd_brand_refresh') ? styles.resizeImg : '';
  const source = featureIsEnabled('dd_brand_refresh')
    ? `${cdnPrefix}/img2/langs/zh_CN/brand_refresh/Tripadvisor_lockup_horizontal_secondary_registered.svg`
    : `${cdnPrefix}/img2/brand_refresh/Tripadvisor_lockup_horizontal_secondary_registered.svg`;

  const img = (
    <img
      src={source}
      alt={localize('common_TripAdvisor') /* Tripadvisor */}
      className={imageClass}
    />
  );

  return (
    <div
      className={classNames(styles.logoContainer, {
        [styles.centerLogo]: props.isMobileBreakpoint,
        [styles.obsoleteBrowser]: props.isObsoleteBrowser,
      })}
    >
      <ClickTracker action="click" module={`TopNav_${props.trackingUrl}`} context="TAlogo">
        {!props.isT4BNavPage ? (
          <Link
            href="/"
            className={styles.logo}
            onClick={event => {
              // eslint-disable-next-line no-restricted-globals
              window.open(props.logoLink, '_self');
              event.preventDefault();
            }}
          >
            {img}
          </Link>
        ) : (
          <Link to={{ page: 'Owners' }} className={styles.logo}>
            {img}
          </Link>
        )}
      </ClickTracker>
    </div>
  );
};

Logo.defaultProps = {
  logoLink: '/',
};

/** Takes in the state and produced the parameters needed for the logo */
const getLogoParams = state => {
  const paramMap = {
    servlet: state.meta.initialServletName,
    locale: state.i18n.locale,
    geoId: state.route.geo,
    viewportCategory: state.meta.device.viewportCategory,
    latitude: state.route.latitude,
    longitude: state.route.longitude,
  };

  // Filter out null and undefined values
  const filteredParams = {};
  Object.keys(paramMap).forEach(key => {
    if (paramMap[key] !== null && typeof paramMap[key] !== 'undefined') {
      filteredParams[key] = paramMap[key];
    }
  });

  return new URLSearchParams(filteredParams);
};

/** Makes the api logo request and then passes the response to LogoContainer */
export default bind<Props, {||}, _, _, _, _>((state, stage) => {
  const logoApiCall = stage(`/data/1.0/brand/logo?${getLogoParams(state).toString()}`);

  return newState => {
    const response = logoApiCall.get();
    return response.loading || typeof response.data === 'undefined' || response.data === null
      ? { loading: true, loaded: false }
      : {
          loading: false,
          loaded: true,
          logoLink: response.data.logoLink,
          isMobileBreakpoint:
            newState.meta.device.viewportCategory === 'MOBILE' &&
            newState.meta.forceFullSite === false,
          isObsoleteBrowser:
            newState.meta.device.browser.family === 'IE' &&
            newState.meta.device.browser.majorVersion <= 10,
          isT4BNavPage: t4bNavPages.includes(newState.route.page),
          trackingUrl: newState.tracking.analytics.url
            ? newState.tracking.analytics.url.substring(1)
            : '',
        };
  };
})(DataCacher()(Logo));
